import React, { useEffect, useState } from 'react';
import Grid from '@material-ui/core/Grid';
import TextField from 'components/TextField';
import Button from 'components/Button';
import Tooltip from '@material-ui/core/Tooltip';
import LockIcon from '@material-ui/icons/Lock';
import LockOpenIcon from '@material-ui/icons/LockOpen';
import Slider from 'react-slick';
import { makeStyles } from '@material-ui/core/styles';
import Tabs from '@material-ui/core/Tabs';
import Tab from '@material-ui/core/Tab';
import Box from '@material-ui/core/Box';
import AppBar from '@material-ui/core/AppBar';
import {
  Container,
  ProductInformation,
  Update,
  ImageContainer,
  ArtDiv,
} from 'styles/pages/edit-products/editProductInformation';

const settings = {
  dots: true,
  infinite: false,
  speed: 500,
  slidesToShow: 1,
  slidesToScroll: 1,
};

const useStyles = makeStyles({
  root: () => ({
    '& .MuiTab-textColorPrimary.Mui-selected': {
      color: '#2280c1',
    },
    '& .MuiTabs-indicator': {
      backgroundColor: '#2280c1',
    },
    '& .MuiTab-wrapper': {
      fontWeight: '600',
    },
  }),
});

const TabPanel = (props) => {
  const { children, value, index, ...other } = props;

  return (
    <div hidden={value !== index} {...other}>
      {value === index && <Box p={3}>{children}</Box>}
    </div>
  );
};

const EditProductDesignerSettings = ({
  product,
  editAdditionalTechniquesArtSettings,
  updatePrintArea,
}) => {
  const classes = useStyles();
  const [tab, setTab] = useState(0);
  const handleTabChange = (event, newValue) => setTab(newValue);

  const [printAreas, setPrintAreas] = useState(product?.PrintAreas);

  const [additionalTechniquesArtSettings, setAdditionalTechniquesArtSettings] =
    useState(product?.AdditionalTechniquesArtSettings);

  const isSub = product?.Product?.PrintType === 'SUB';

  const handleAdditionalAreaSettingChange = (
    parentIndex,
    artSettingsIndex,
    data
  ) =>
    setAdditionalTechniquesArtSettings(
      additionalTechniquesArtSettings?.map((b, x) => {
        if (parentIndex !== x) {
          return b;
        }
        return {
          ...b,
          artSettings: b?.artSettings?.map((c, y) => {
            if (y !== artSettingsIndex) {
              return c;
            }
            return {
              ...c,
              areaSettings: {
                ...c?.areaSettings,
                ...data,
              },
            };
          }),
        };
      })
    );

  const handlePrintAreasDesignerChange = (index, data) =>
    setPrintAreas(
      printAreas?.map((b, x) => {
        if (index !== x) {
          return b;
        }
        return {
          ...b,
          designerSettings: {
            ...b?.designerSettings,
            ...data,
          },
        };
      })
    );

  return (
    <Container>
      <React.Fragment>
        <ProductInformation>
          <h3>Product Designer Settings</h3>

          <Slider {...settings}>
            {printAreas?.map((printArea, index) => {
              const designerSettings = printArea?.designerSettings;
              const isLocked = !!designerSettings?.locked;

              return (
                <div
                  key={`${printArea?.printAreaName} ${product?.Product?.PrintType} ${index}`}
                >
                  <h4>
                    {printArea?.printAreaName} ({product?.Product?.PrintType})
                  </h4>
                  <Grid container spacing={2}>
                    <Grid item xs={6} sm={2} md={2}>
                      <TextField
                        label="Machine Width (Inch)"
                        size="small"
                        type="number"
                        disabled={isLocked}
                        value={designerSettings?.platenWidthInch || 0}
                        onChange={(platenWidthInch) =>
                          handlePrintAreasDesignerChange(index, {
                            platenWidthInch: Number(platenWidthInch),
                          })
                        }
                      />
                    </Grid>
                    <Grid item xs={6} sm={2} md={2}>
                      <TextField
                        label="Machine Height (Inch)"
                        size="small"
                        type="number"
                        disabled={isLocked}
                        value={designerSettings?.platenHeightInch || 0}
                        onChange={(platenHeightInch) => {
                          handlePrintAreasDesignerChange(index, {
                            platenHeightInch: Number(platenHeightInch),
                          });
                        }}
                      />
                    </Grid>
                    <Grid item xs={6} sm={2} md={2}>
                      <TextField
                        label="Multiplier"
                        size="small"
                        type="number"
                        disabled={true}
                        value={designerSettings?.multiplier || 0}
                      />
                    </Grid>
                    <Tooltip title={isLocked ? 'Locked' : 'Un-Locked'}>
                      {isLocked ? (
                        <LockIcon
                          onClick={() => {
                            handlePrintAreasDesignerChange(index, {
                              locked: false,
                            });
                          }}
                          style={{
                            marginTop: 15,
                            marginLeft: 20,
                            cursor: 'pointer',
                            color: '#000',
                          }}
                        />
                      ) : (
                        <LockOpenIcon
                          onClick={() => {
                            handlePrintAreasDesignerChange(index, {
                              top: 0,
                              bottom: 0,
                              left: 0,
                              right: 0,
                              width: 0,
                              height: 0,
                              locked: true,
                            });
                          }}
                          style={{
                            marginTop: 15,
                            marginLeft: 20,
                            cursor: 'pointer',
                            color: '#000',
                          }}
                        />
                      )}
                    </Tooltip>
                  </Grid>

                  <Grid container spacing={2} style={{ marginTop: 20 }}>
                    <Grid item xs={6} sm={2} md={2}>
                      <TextField
                        label="Top"
                        size="small"
                        type="number"
                        disabled={!isLocked}
                        value={designerSettings?.top || 0}
                        onChange={(top) =>
                          handlePrintAreasDesignerChange(index, {
                            top: Number(top),
                          })
                        }
                      />
                    </Grid>
                    <Grid item xs={6} sm={2} md={2}>
                      <TextField
                        label="Bottom"
                        size="small"
                        type="number"
                        disabled={!isLocked}
                        value={designerSettings?.bottom || 0}
                        onChange={(bottom) =>
                          handlePrintAreasDesignerChange(index, {
                            bottom: Number(bottom),
                          })
                        }
                      />
                    </Grid>
                    <Grid item xs={6} sm={2} md={2}>
                      <TextField
                        label="Left"
                        size="small"
                        type="number"
                        disabled={!isLocked}
                        value={designerSettings?.left || 0}
                        onChange={(left) =>
                          handlePrintAreasDesignerChange(index, {
                            left: Number(left),
                          })
                        }
                      />
                    </Grid>
                    <Grid item xs={6} sm={2} md={2}>
                      <TextField
                        label="Right"
                        size="small"
                        type="number"
                        disabled={!isLocked}
                        value={designerSettings?.right || 0}
                        onChange={(right) =>
                          handlePrintAreasDesignerChange(index, {
                            right: Number(right),
                          })
                        }
                      />
                    </Grid>
                    <Grid item xs={6} sm={2} md={2}>
                      <TextField
                        label="Width"
                        size="small"
                        type="number"
                        disabled={
                          !designerSettings?.platenWidthInch ||
                          !designerSettings?.platenHeightInch ||
                          !isLocked
                        }
                        value={designerSettings?.width || 0}
                        onChange={(width) => {
                          const multiplier = Number(
                            (designerSettings?.platenWidthInch *
                              (isSub ? 300 : 255)) /
                              width
                          ).toFixed(1);
                          const height = Math.round(
                            (designerSettings?.platenHeightInch *
                              (isSub ? 300 : 255)) /
                              multiplier
                          );
                          handlePrintAreasDesignerChange(index, {
                            width: Number(width),
                            multiplier: Number(multiplier),
                            height: Number(height),
                          });
                        }}
                      />
                    </Grid>
                    <Grid item xs={6} sm={2} md={2}>
                      <TextField
                        label="Height"
                        size="small"
                        type="number"
                        value={designerSettings?.height || 0}
                        disabled={
                          !designerSettings?.platenWidthInch ||
                          !designerSettings?.platenHeightInch ||
                          !isLocked
                        }
                        onChange={(height) => {
                          const multiplier = Number(
                            (designerSettings?.platenHeightInch *
                              (isSub ? 300 : 255)) /
                              height
                          ).toFixed(1);
                          const width = Math.round(
                            (designerSettings?.platenWidthInch *
                              (isSub ? 300 : 255)) /
                              multiplier
                          );

                          handlePrintAreasDesignerChange(index, {
                            width: Number(width),
                            multiplier: Number(multiplier),
                            height: Number(height),
                          });
                        }}
                      />
                    </Grid>
                  </Grid>

                  <ImageContainer>
                    <img src={printArea?.printAreaMarketingImage} />
                    <ArtDiv
                      style={{
                        top: `${designerSettings?.top || 0}px`,
                        left: `${designerSettings?.left || 0}px`,
                        right: `${designerSettings?.right || 0}px`,
                        bottom: `${designerSettings?.bottom || 0}px`,
                        width: `${designerSettings?.width || 0}px`,
                        height: `${designerSettings?.height || 0}px`,
                        boxShadow: isSub
                          ? '4px 4px 10px #d9d9d9, -4px -4px 10px #ffffff'
                          : 'none',
                      }}
                    ></ArtDiv>
                  </ImageContainer>
                </div>
              );
            })}
          </Slider>
        </ProductInformation>
        <Update>
          <Button title="Update" onClick={() => updatePrintArea(printAreas)} />
        </Update>
      </React.Fragment>

      {additionalTechniquesArtSettings?.length > 0 && (
        <React.Fragment>
          <hr style={{ margin: '50px 0 0 0' }} />
          <div className={classes.root}>
            <div style={{ display: 'flex' }}>
              <AppBar
                position="static"
                color="default"
                style={{ flex: 1, marginRight: 20 }}
              >
                <Tabs value={tab} onChange={handleTabChange}>
                  {additionalTechniquesArtSettings?.map((a, i) => (
                    <Tab
                      label={`${a?.name} Settings`}
                      style={{ fontWeight: 'bold' }}
                    />
                  ))}
                </Tabs>
              </AppBar>
              <Update style={{ margin: 0 }}>
                <Button
                  title="Update"
                  onClick={() => {
                    const payload = {
                      ID: product?.Product?.ID,
                      additionalTechniquesArtSettings,
                    };

                    editAdditionalTechniquesArtSettings(payload);
                  }}
                />
              </Update>
            </div>

            {additionalTechniquesArtSettings?.map((a, i) => {
              const artSettings = a?.artSettings;

              return (
                <TabPanel value={tab} index={i}>
                  <ProductInformation key={a?.name}>
                    <Slider {...settings}>
                      {artSettings?.map((artSetting, j) => {
                        const areaName = artSetting?.areaName;
                        const areaSettings = artSetting?.areaSettings;
                        const isLocked = areaSettings?.locked;

                        return (
                          <div key={`${areaName}_${i}_${j}`}>
                            <h4>
                              {artSetting?.areaName} ({a?.name})
                            </h4>
                            <Grid container spacing={2}>
                              <Grid item xs={6} sm={2} md={2}>
                                <TextField
                                  label="Machine Width (Inch)"
                                  size="small"
                                  type="number"
                                  disabled={isLocked}
                                  value={areaSettings?.platenWidthInch || 0}
                                  onChange={(platenWidthInch) =>
                                    handleAdditionalAreaSettingChange(i, j, {
                                      platenWidthInch: Number(platenWidthInch),
                                    })
                                  }
                                />
                              </Grid>
                              <Grid item xs={6} sm={2} md={2}>
                                <TextField
                                  label="Machine Height (Inch)"
                                  size="small"
                                  type="number"
                                  disabled={isLocked}
                                  value={areaSettings?.platenHeightInch || 0}
                                  onChange={(platenHeightInch) =>
                                    handleAdditionalAreaSettingChange(i, j, {
                                      platenHeightInch:
                                        Number(platenHeightInch),
                                    })
                                  }
                                />
                              </Grid>
                              <Grid item xs={6} sm={2} md={2}>
                                <TextField
                                  label="Multiplier"
                                  size="small"
                                  type="number"
                                  disabled={true}
                                  value={areaSettings?.multiplier || 0}
                                />
                              </Grid>

                              {a?.name === 'Plus' && (
                                <Grid item xs={6} sm={2} md={2}>
                                  <TextField
                                    label="Art Board Price"
                                    size="small"
                                    type="number"
                                    value={areaSettings?.artboardPrice || 0}
                                    onChange={(artboardPrice) =>
                                      handleAdditionalAreaSettingChange(i, j, {
                                        artboardPrice: Number(artboardPrice),
                                      })
                                    }
                                  />
                                </Grid>
                              )}

                              <Tooltip
                                title={isLocked ? 'Locked' : 'Un-Locked'}
                              >
                                {isLocked ? (
                                  <LockIcon
                                    onClick={() =>
                                      handleAdditionalAreaSettingChange(i, j, {
                                        locked: false,
                                      })
                                    }
                                    style={{
                                      marginTop: 15,
                                      marginLeft: 20,
                                      cursor: 'pointer',
                                      color: '#000',
                                    }}
                                  />
                                ) : (
                                  <LockOpenIcon
                                    onClick={() => {
                                      handleAdditionalAreaSettingChange(i, j, {
                                        top: 0,
                                        bottom: 0,
                                        left: 0,
                                        right: 0,
                                        width: 0,
                                        height: 0,
                                        locked: true,
                                      });
                                    }}
                                    style={{
                                      marginTop: 15,
                                      marginLeft: 20,
                                      cursor: 'pointer',
                                      color: '#000',
                                    }}
                                  />
                                )}
                              </Tooltip>
                            </Grid>

                            <Grid
                              container
                              spacing={2}
                              style={{ marginTop: 20 }}
                            >
                              <Grid item xs={6} sm={2} md={2}>
                                <TextField
                                  label="Top"
                                  size="small"
                                  type="number"
                                  disabled={!isLocked}
                                  value={areaSettings?.top || 0}
                                  onChange={(top) =>
                                    handleAdditionalAreaSettingChange(i, j, {
                                      top: Number(top),
                                    })
                                  }
                                />
                              </Grid>
                              <Grid item xs={6} sm={2} md={2}>
                                <TextField
                                  label="Bottom"
                                  size="small"
                                  type="number"
                                  disabled={!isLocked}
                                  value={areaSettings?.bottom || 0}
                                  onChange={(bottom) =>
                                    handleAdditionalAreaSettingChange(i, j, {
                                      bottom: Number(bottom),
                                    })
                                  }
                                />
                              </Grid>
                              <Grid item xs={6} sm={2} md={2}>
                                <TextField
                                  label="Left"
                                  size="small"
                                  type="number"
                                  disabled={!isLocked}
                                  value={areaSettings?.left || 0}
                                  onChange={(left) =>
                                    handleAdditionalAreaSettingChange(i, j, {
                                      left: Number(left),
                                    })
                                  }
                                />
                              </Grid>
                              <Grid item xs={6} sm={2} md={2}>
                                <TextField
                                  label="Right"
                                  size="small"
                                  type="number"
                                  disabled={!isLocked}
                                  value={areaSettings?.right || 0}
                                  onChange={(right) =>
                                    handleAdditionalAreaSettingChange(i, j, {
                                      right: Number(right),
                                    })
                                  }
                                />
                              </Grid>
                              <Grid item xs={6} sm={2} md={2}>
                                <TextField
                                  label="Width"
                                  size="small"
                                  type="number"
                                  disabled={
                                    !areaSettings?.platenWidthInch ||
                                    !areaSettings?.platenHeightInch ||
                                    !isLocked
                                  }
                                  value={areaSettings?.width || 0}
                                  onChange={(width) => {
                                    const multiplier = Number(
                                      (areaSettings?.platenWidthInch *
                                        (isSub ? 300 : 255)) /
                                        width
                                    ).toFixed(1);
                                    const height = Math.round(
                                      (areaSettings?.platenHeightInch *
                                        (isSub ? 300 : 255)) /
                                        multiplier
                                    );

                                    handleAdditionalAreaSettingChange(i, j, {
                                      width: Number(width),
                                      multiplier: Number(multiplier),
                                      height: Number(height),
                                    });
                                  }}
                                />
                              </Grid>
                              <Grid item xs={6} sm={2} md={2}>
                                <TextField
                                  label="Height"
                                  size="small"
                                  type="number"
                                  value={areaSettings?.height || 0}
                                  disabled={
                                    !areaSettings?.platenWidthInch ||
                                    !areaSettings?.platenHeightInch ||
                                    !isLocked
                                  }
                                  onChange={(height) => {
                                    const multiplier = Number(
                                      (areaSettings?.platenHeightInch *
                                        (isSub ? 300 : 255)) /
                                        height
                                    ).toFixed(1);
                                    const width = Math.round(
                                      (areaSettings?.platenWidthInch *
                                        (isSub ? 300 : 255)) /
                                        multiplier
                                    );

                                    handleAdditionalAreaSettingChange(i, j, {
                                      height: Number(height),
                                      width: Number(width),
                                      multiplier: Number(multiplier),
                                    });
                                  }}
                                />
                              </Grid>
                            </Grid>

                            <ImageContainer>
                              <img
                                src={
                                  artSetting?.image ||
                                  product?.Product?.MarketingImagePath
                                }
                              />
                              <ArtDiv
                                style={{
                                  top: `${areaSettings?.top}px`,
                                  left: `${areaSettings?.left}px`,
                                  right: `${areaSettings?.right}px`,
                                  bottom: `${areaSettings?.bottom}px`,
                                  width: `${areaSettings?.width}px`,
                                  height: `${areaSettings?.height}px`,
                                  boxShadow: isSub
                                    ? '4px 4px 10px #d9d9d9, -4px -4px 10px #ffffff'
                                    : 'none',
                                }}
                              ></ArtDiv>
                            </ImageContainer>
                          </div>
                        );
                      })}
                    </Slider>
                  </ProductInformation>
                </TabPanel>
              );
            })}
          </div>
        </React.Fragment>
      )}
    </Container>
  );
};

export default EditProductDesignerSettings;
